import {
  Button,
  Input,
  Field,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens
} from '@fluentui/react-components';
import { Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle } from '@fluentui/react-components/unstable';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../redux/reducers';
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import { ServiceCategory, NewServiceCategory } from '../../../models/Entities/ServiceCategory';

// i18Next
import { useTranslation, Trans } from 'react-i18next';

// store
import * as ServiceCategoryStore from '../../../redux/middleware/ServiceCategoryMiddleware';

interface FormValidation {
  success: boolean;
  errors: string[];
}

export interface IProps {
  isOpen: boolean;
  dismissPanel: () => void;
  ServiceCategory?: ServiceCategory;
}

export const ServiceCategoryFormComponent = (props: IProps) => {
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [FormValidationStatus, setFormValidationStatus] = useState<
    FormValidation | undefined
  >();

  const dispatch = useDispatch();
  const [t] = useTranslation();

  // state
  const ServiceCategoryState = useSelector((appState: AppState) => appState.ServiceCategories);

  // entity properties
  const [description, setDescription] = useState<string>('');

  // effects
  useEffect(() => {
    if (props.ServiceCategory) {
      setDescription(props.ServiceCategory.description || '');
    } else {
      ResetForm();
    }
  }, [props.ServiceCategory]);

  useEffect(() => {
    if (
      submittedForm &&
      ServiceCategoryState &&
      (ServiceCategoryState.isAddingSuccess || ServiceCategoryState.isSavingSuccess)
    ) {
      // Small delay to ensure state is updated before closing
      setTimeout(() => {
        ResetForm();
        props.dismissPanel();
      }, 100);
    }
  }, [
    submittedForm,
    ServiceCategoryState?.isAddingSuccess,
    ServiceCategoryState?.isSavingSuccess,
    props.dismissPanel
  ]);

  const ResetForm = () => {
    setDescription('');
    setSubmittedForm(false);
    setFormValidationStatus(undefined);
  };

  // Events
  const onChangeDescription = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(ev.target.value);
  };

  const HandleSaveForm = () => {
    setSubmittedForm(true);
    if (ValidateForm()) {
      if (props.ServiceCategory) {
        // Update existing category
        const updatedCategory: ServiceCategory = {
          id: props.ServiceCategory.id,
          description: description,
          created: props.ServiceCategory.created,
          createdBy: props.ServiceCategory.createdBy
        };
        dispatch(ServiceCategoryStore.actionCreators.UpdateServiceCategory(updatedCategory));
      } else {
        // Add new category
        const newCategory: NewServiceCategory = {
          description: description
        };
        dispatch(ServiceCategoryStore.actionCreators.AddServiceCategory(newCategory));
      }
    }
  };

  const HandleCancelForm = () => {
    ResetForm();
    props.dismissPanel();
  };

  // form validation
  const ValidateForm = (): boolean => {
    let validationResult: boolean = true;
    let FormValidation: FormValidation = { success: true, errors: [] };
    setFormValidationStatus(FormValidation);

    if (!description || description.trim() === '') {
      validationResult = false;
      FormValidation.success = false;
      FormValidation.errors.push(t('serviceCategoryForm.validation.requiredDescription'));
    }

    if (description && description.length > 200) {
      validationResult = false;
      FormValidation.success = false;
      FormValidation.errors.push(t('serviceCategoryForm.validation.descriptionTooLong'));
    }

    FormValidation.success = validationResult;
    setFormValidationStatus(FormValidation);
    return validationResult;
  };

  const styles = useStyles();

  const formFields = (
    <div className={styles.formContainer}>
      <Field
        label={t('serviceCategoryForm.fields.description')}
        required
        validationMessage={
          submittedForm === true && (!description || description.trim() === '')
            ? t('serviceCategoryForm.validation.requiredDescription')
            : undefined
        }
      >
        <Input
          name="txt_description"
          onChange={onChangeDescription}
          value={description}
          placeholder={t('serviceCategoryForm.fields.descriptionPlaceholder')}
        />
      </Field>
    </div>
  );

  return (
    <Drawer
      open={props.isOpen}
      onOpenChange={(_, { open }) => !open && props.dismissPanel()}
      position="end"
      size="small"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={props.dismissPanel}
            />
          }
        >
          {props.ServiceCategory
            ? t('serviceCategoryForm.titleEdit')
            : t('serviceCategoryForm.title')}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        <div className={styles.container}>
          {submittedForm && !FormValidationStatus?.success && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>{t('serviceCategoryForm.reviewInformation')}</MessageBarTitle>
                <ul>
                  {FormValidationStatus?.errors.map(
                    (Error: string, index: number) => (
                      <li key={index}>{Error}</li>
                    )
                  )}
                </ul>
              </MessageBarBody>
            </MessageBar>
          )}

          {ServiceCategoryState &&
            (ServiceCategoryState.failOnAddingNewOne ||
              ServiceCategoryState.failOnUpdatingOne) &&
            ServiceCategoryState.error && (
              <ErrorMessageComponent Error={ServiceCategoryState.error} />
            )}

          {formFields}
        </div>
      </DrawerBody>

      <DrawerFooter>
        <Button onClick={HandleCancelForm}>
          <Trans i18nKey={'serviceCategoryForm.cancelButton'} />
        </Button>
        <Button
          appearance="primary"
          onClick={HandleSaveForm}
          disabled={ServiceCategoryState?.isAdding || ServiceCategoryState?.isSaving}
        >
          {ServiceCategoryState?.isAdding || ServiceCategoryState?.isSaving ? (
            <Spinner size="tiny" />
          ) : (
            <Trans i18nKey={'serviceCategoryForm.saveButton'} />
          )}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM
  }
});

export default ServiceCategoryFormComponent;
